<template>
  <App class="documents-page documents-editor-page" :class="{ 'documents-page--tools': toolsLayout }">
    <AppBar :closable="!hideBarClose">
      <div class="documents-top-bar">
        <Button
          v-if="toolsLayout"
          class="documents-menu-btn"
          title="文档目录"
          @click="toggleTreeDrawer"
        >
          <Icon type="ios-list" :size="22" />
        </Button>
        <div class="documents-bar-left">
          <BaseIcon icon="md-create" :size="20" color="var(--primary)" />
          <div class="documents-bar-titles">
            <span class="documents-bar-title">{{ rootTitle }}</span>
            
          </div>
          <Tag v-if="!isNarrowToolsViewport" color="blue" style="margin-left: 8px;">文档编辑</Tag>
        </div>
        <div class="documents-bar-actions">
          <Button
            class="documents-action-btn"
            size="small"
            title="预览"
            @click="goView"
          >
            <Icon v-if="isNarrowToolsViewport" type="ios-eye-outline" :size="20" />
            <span v-if="!isNarrowToolsViewport">预览</span>
          </Button>
          <Button
            class="documents-action-btn"
            size="small"
            title="分享目录"
            @click="copyPublicShareUrl"
          >
            <Icon v-if="isNarrowToolsViewport" type="ios-share-outline" :size="20" />
            <span v-if="!isNarrowToolsViewport">分享</span>
          </Button>
          <Button
            class="documents-action-btn"
            size="small"
            title="分享此页"
            :disabled="!selectedDocId"
            @click="copySingleDocumentShareUrl"
          >
            <Icon v-if="isNarrowToolsViewport" type="ios-link-outline" :size="20" />
            <span v-if="!isNarrowToolsViewport">分享此页</span>
          </Button>
        </div>
      </div>
    </AppBar>

    <AppMask v-if="pageLoading" />

    <div
      v-if="toolsLayout && isNarrowToolsViewport && treeDrawerOpen"
      class="documents-tree-overlay"
      @click="closeTreeDrawer"
    />

    <AppLeft
      class="documents-app-left"
      :collapse="treePanelCollapsed"
      :width="treePanelWidth"
    >
      <DocumentTree
        ref="documentTree"
        :tree="tree"
        :root-id="directoryId"
        :selected-id="selectedDocId"
        :loading="treeLoading"
        :uploading="uploading"
        editor-mode
        @select="onTreeDocSelect"
        @refresh="loadTree"
        @upload-files="handleUploadFiles"
        @dir-action="handleDirAction"
        @file-action="handleFileAction"
        @drop-in="handleTreeDropIn"
      />
    </AppLeft>

    <AppClient :collapse-left="clientCollapseLeft" :left-width="treePanelWidth">
      <div class="documents-editor-body-wrap">
        <div class="documents-editor-main">
          <div v-if="contentLoading" class="documents-editor-state">
            <Spin size="large">加载中...</Spin>
          </div>
          <div v-else-if="!currentDoc" class="documents-editor-state documents-editor-state--empty">
            <BaseEmpty type="empty" msg="请选择 text / md 文档进行编辑" />
          </div>
          <div v-else-if="!canEdit" class="documents-editor-state documents-editor-state--empty">
            <BaseEmpty type="content" :msg="`「${docTitle}」为附件类型，不支持在线编辑`" />
          </div>
        <div v-else class="documents-editor-panel">
          <div class="documents-editor-header">
            <Input
              v-model="editName"
              class="documents-editor-name-input"
              placeholder="文档名称"
              :maxlength="80"
            />
            <Button
              v-if="nameDirty"
              type="primary"
              size="small"
              icon="md-checkmark"
              :loading="nameSaving"
              @click="saveDocumentName"
            >
              保存名称
            </Button>
            <Tag v-if="editKind === 'text'" color="default">文本</Tag>
            <Tag v-else color="blue">Markdown</Tag>
            <span v-if="contentDirty" class="documents-editor-dirty">内容未保存</span>
            <Button
              class="documents-editor-save-btn"
              type="primary"
              size="small"
              icon="md-checkmark"
              :loading="saving"
              :disabled="!canEdit"
              @click="saveDocument"
            >
              保存
            </Button>
          </div>
          <BaseMarkdownEditor
            v-if="editKind === 'md'"
            :key="editorMountKey"
            v-model="editContent"
            :height="'calc(100vh - 170px)'"
            class="documents-editor-body"
            @input="onEditContentInput"
          />
          <Input
            v-else
            v-model="editContent"
            type="textarea"
            :rows="28"
            class="documents-editor-textarea"
            placeholder="输入文本内容"
            @on-change="onEditContentInput"
          />
        </div>
        </div>
      </div>
    </AppClient>

    <div
      v-if="toolsLayout && canEdit && currentDoc"
      class="documents-mobile-save-fab"
    >
      <Button
        type="primary"
        icon="md-checkmark"
        :loading="saving"
        @click="saveDocument"
      >
        保存
      </Button>
    </div>

    <Modal
      v-model="showFileRename"
      title="重命名文件"
      footer-hide
      :mask-closable="false"
    >
      <Form :label-width="72">
        <FormItem label="名称">
          <Input v-model="fileRenameForm.name" placeholder="文件名称" :maxlength="80" />
        </FormItem>
      </Form>
      <div class="documents-modal-footer">
        <Button @click="showFileRename = false">取消</Button>
        <Button type="primary" :loading="fileRenameSaving" @click="submitFileRename">确定</Button>
      </div>
    </Modal>

    <Modal
      v-model="showDirEditor"
      :title="dirForm.id ? '编辑目录' : '新建目录'"
      footer-hide
      :mask-closable="false"
    >
      <Form :label-width="88">
        <FormItem label="目录名称">
          <Input v-model="dirForm.name" placeholder="请输入目录名称" :maxlength="50" />
        </FormItem>
        <FormItem label="描述">
          <Input
            v-model="dirForm.description"
            type="textarea"
            :rows="3"
            placeholder="可选"
            :maxlength="128"
          />
        </FormItem>
      </Form>
      <div class="documents-modal-footer">
        <Button @click="showDirEditor = false">取消</Button>
        <Button type="primary" :loading="dirSaving" @click="submitDirForm">确定</Button>
      </div>
    </Modal>
  </App>
</template>

<script>
import App from '@/components/app/app';
import AppBar from '@/components/app/bar';
import AppMask from '@/components/app/mask';
import AppLeft from '@/components/app/left';
import AppClient from '@/components/app/client';
import BaseEmpty from '@/components/base/Empty';
import BaseIcon from '@/components/base/Icon';
import DocumentTree from './DocumentTree.vue';
import {
  findSelectableFileById,
  flattenSelectableFiles,
  getDocumentTitle,
  getFileExt,
  getMarkdownContent,
  isDirIdInTree,
  isEditableDocument,
  MD_EXTS,
  TEXT_UPLOAD_EXTS,
  normalizeTreeRoot,
  patchTreeNode,
  sortDocumentTree,
} from './document-helpers';
import documentsToolsLayout from './mixins/documents-tools-layout';
import documentsShare from './mixins/documents-share';

export default {
  name: 'DocumentsEditor',
  mixins: [documentsToolsLayout, documentsShare],
  props: {
    embedDirId: {
      type: String,
      default: '',
    },
    hideBarClose: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    App,
    AppBar,
    AppMask,
    AppLeft,
    AppClient,
    BaseEmpty,
    BaseIcon,
    DocumentTree,
  },
  routerOption: {
    as_object: true,
  },
  data() {
    return {
      pageLoading: true,
      treeLoading: false,
      contentLoading: false,
      saving: false,
      uploading: false,
      uploadDirOverride: null,
      dirSaving: false,
      contentDirty: false,
      contentBaseline: '',
      nameBaseline: '',
      editName: '',
      editorMountKey: 0,
      contentReady: false,
      nameSaving: false,
      fileRenameSaving: false,
      showFileRename: false,
      fileRenameForm: { id: null, name: '' },
      tree: [],
      rootTitle: '在线文档',
      selectedDocId: '',
      currentDoc: null,
      editContent: '',
      showDirEditor: false,
      dirForm: {
        id: null,
        name: '',
        type: 'dir',
        parent_id: null,
        description: '',
        remote_api: '',
      },
    };
  },
  computed: {
    directoryId() {
      return this.embedDirId || this.$route.params.id;
    },
    rootNodes() {
      return normalizeTreeRoot(this.tree);
    },
    canEdit() {
      return isEditableDocument(this.currentDoc);
    },
    editKind() {
      if (!this.currentDoc) return '';
      const type = String(this.currentDoc.type || '').toLowerCase();
      if (type === 'text') return 'text';
      return 'md';
    },
    docTitle() {
      return getDocumentTitle(this.currentDoc);
    },
    nameDirty() {
      if (!this.contentReady || !this.currentDoc) return false;
      return (this.editName || '').trim() !== (this.nameBaseline || '').trim();
    },
  },
  watch: {
    directoryId: {
      handler(id) {
        if (id) this.loadTree();
      },
      immediate: true,
    },
    '$route.query.doc': {
      handler(docId) {
        if (docId) {
          this.selectDocumentById(docId);
        }
      },
    },
  },
  methods: {
    loadTree() {
      if (!this.directoryId) return;
      this.pageLoading = true;
      this.treeLoading = true;
      this.$api.afterLogin(() => {
        this.$api.get(`knowledge/directories/${this.directoryId}`)
          .then((res) => {
            const data = res.data.data;
            this.tree = sortDocumentTree(data);
            const root = normalizeTreeRoot(data)[0];
            this.rootTitle = (root && root.name) || '在线文档';
            this.pageLoading = false;
            this.treeLoading = false;
            this.initSelection();
          })
          .catch((err) => {
            this.pageLoading = false;
            this.treeLoading = false;
            this.$Message.error(`加载目录失败: ${err}`);
          });
      });
    },
    initSelection() {
      const queryDoc = this.$route.query.doc;
      if (queryDoc) {
        this.selectDocumentById(queryDoc);
        return;
      }
      const files = flattenSelectableFiles(this.rootNodes);
      const editable = files.filter((f) => isEditableDocument(f));
      if (editable.length) {
        this.handleDocSelect(editable[0], false);
      } else if (files.length) {
        this.handleDocSelect(files[0], false);
      }
    },
    selectDocumentById(id) {
      const doc = findSelectableFileById(this.rootNodes, id);
      if (doc) {
        this.handleDocSelect(doc, false);
      }
    },
    handleDirAction(payload) {
      const {action, dir, docType} = payload || {};
      if (!dir && action !== 'add') return;
      if (action === 'add') {
        this.dirForm = {
          id: null,
          name: '',
          type: 'dir',
          parent_id: dir.id,
          description: '',
          remote_api: '',
        };
        this.showDirEditor = true;
      } else if (action === 'edit') {
        if (String(dir.id) === String(this.directoryId)) {
          this.$Message.warning('根目录不可编辑');
          return;
        }
        this.dirForm = {
          id: dir.id,
          name: dir.name || '',
          type: dir.type || 'dir',
          parent_id: dir.parent_id,
          description: dir.description || '',
          remote_api: dir.remote_api || '',
        };
        this.showDirEditor = true;
      } else if (action === 'remove') {
        if (String(dir.id) === String(this.directoryId)) {
          this.$Message.warning('根目录不可删除');
          return;
        }
        this.$Modal.confirm({
          title: '删除确认',
          content: `确定删除目录「${dir.name}」？`,
          onOk: () => this.deleteDirectory(dir),
        });
      } else if (action === 'add-doc') {
        this.createDocument(dir, docType || 'md');
      } else if (action === 'upload-files' && dir) {
        this.uploadDirOverride = dir.id;
        this.$nextTick(() => {
          const tree = this.$refs.documentTree;
          if (tree && tree.openFilePicker) tree.openFilePicker();
        });
      }
    },
    handleFileAction(payload) {
      const {action, file} = payload || {};
      if (action === 'rename' && file) {
        this.fileRenameForm = {
          id: file.id,
          name: file.name || '',
        };
        this.showFileRename = true;
      } else if (action === 'remove' && file) {
        this.$Modal.confirm({
          title: '删除确认',
          content: `确定删除「${file.name || '该文件'}」？`,
          onOk: () => this.deleteFile(file),
        });
      }
    },
    submitFileRename() {
      const id = this.fileRenameForm.id;
      const name = (this.fileRenameForm.name || '').trim();
      if (id == null || !name) {
        this.$Message.warning('请输入名称');
        return;
      }
      this.fileRenameSaving = true;
      this.$api.put(`knowledge/data/${id}`, {name})
        .then(() => {
          this.$Message.success('重命名成功');
          this.showFileRename = false;
          if (this.currentDoc && String(this.currentDoc.id) === String(id)) {
            this.editName = name;
            this.nameBaseline = name;
            this.currentDoc = {...this.currentDoc, name};
          }
          this.loadTree();
        })
        .catch((err) => this.$Message.error(`重命名失败: ${err}`))
        .finally(() => {
          this.fileRenameSaving = false;
        });
    },
    handleTreeDropIn({draggedId, targetDirId}) {
      if (!draggedId || !targetDirId) return;
      if (String(draggedId) === String(targetDirId)) return;

      if (isDirIdInTree(this.rootNodes, draggedId)) {
        if (String(draggedId) === String(this.directoryId)) {
          this.$Message.warning('不能移动根目录');
          return;
        }
        this.$api.put(`/knowledge/directories/${draggedId}`, {parent_id: targetDirId})
          .then(() => {
            this.$Message.success('目录已移动');
            this.loadTree();
          })
          .catch((err) => this.$Message.error(`移动失败: ${err}`));
        return;
      }

      const file = findSelectableFileById(this.rootNodes, draggedId);
      const dirId = (file && file.dir_id) || targetDirId;
      if (file && String(dirId) === String(targetDirId)) return;

      this.$api.put(`/knowledge/data/${draggedId}`, {dir_id: targetDirId})
        .then(() => {
          this.$Message.success('文件已移动');
          this.loadTree();
        })
        .catch((err) => this.$Message.error(`移动失败: ${err}`));
    },
    submitDirForm() {
      const name = (this.dirForm.name || '').trim();
      if (!name) {
        this.$Message.warning('请输入目录名称');
        return;
      }
      this.dirSaving = true;
      const url = this.dirForm.id
        ? `/knowledge/directories/${this.dirForm.id}`
        : '/knowledge/directories';
      const method = this.dirForm.id ? 'put' : 'post';
      this.$api[method](url, {
        name,
        type: this.dirForm.type || 'dir',
        parent_id: this.dirForm.parent_id,
        description: this.dirForm.description || '',
        remote_api: this.dirForm.remote_api || '',
      })
        .then(() => {
          this.$Message.success('保存成功');
          this.showDirEditor = false;
          this.loadTree();
        })
        .catch((err) => this.$Message.error(`保存失败: ${err}`))
        .finally(() => {
          this.dirSaving = false;
        });
    },
    resolveUploadDirId() {
      if (this.uploadDirOverride != null && this.uploadDirOverride !== '') {
        return this.uploadDirOverride;
      }
      if (this.currentDoc && this.currentDoc.dir_id != null && this.currentDoc.dir_id !== '') {
        return this.currentDoc.dir_id;
      }
      return this.directoryId;
    },
    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result || '');
        reader.onerror = () => reject(reader.error || new Error('读取文件失败'));
        reader.readAsText(file, 'UTF-8');
      });
    },
    stripFileBaseName(fileName) {
      const name = String(fileName || '').trim();
      if (!name) return '未命名';
      const idx = name.lastIndexOf('.');
      if (idx <= 0) return name;
      return name.slice(0, idx) || '未命名';
    },
    inferUploadDocType(file) {
      const ext = getFileExt(file && file.name);
      if (TEXT_UPLOAD_EXTS.includes(ext)) return 'text';
      if (MD_EXTS.includes(ext)) return 'md';
      const mime = String((file && file.type) || '').toLowerCase();
      if (mime === 'text/plain') return 'text';
      if (mime === 'text/markdown') return 'md';
      return 'file';
    },
    createKnowledgeRecordFromFile(file, dirId) {
      const docType = this.inferUploadDocType(file);
      const displayName = this.stripFileBaseName(file.name);
      if (docType === 'text') {
        return this.readFileAsText(file).then((content) => this.$api.post('/knowledge/data', {
          type: 'text',
          name: displayName,
          content,
          dir_id: dirId,
        }));
      }
      if (docType === 'md') {
        return this.readFileAsText(file).then((content) => this.$api.post('/knowledge/data', {
          type: 'md',
          name: displayName,
          content,
          markdown_content: content,
          has_markdown: true,
          dir_id: dirId,
        }));
      }
      return Promise.reject(new Error('不支持的文件类型'));
    },
    uploadBinaryFilesToDir(files, dirId) {
      const coskey = this.$store.getters['session/session']
        && this.$store.getters['session/session'].coskey;
      if (!coskey) {
        this.$Message.warning('未配置上传存储，无法上传附件类文件');
        return Promise.resolve({skipped: files.length});
      }
      if (!this.$cos || typeof this.$cos.upload !== 'function') {
        this.$Message.warning('上传组件不可用');
        return Promise.resolve({skipped: files.length});
      }
      return new Promise((resolve, reject) => {
        this.$cos.upload(files, {
          onSuccess: (uploaded) => {
            const list = uploaded || [];
            if (!list.length) {
              resolve({created: 0});
              return;
            }
            Promise.all(list.map((f) => {
              const name = this.stripFileBaseName(f.name);
              return this.$api.post('/knowledge/data', {
                type: 'file',
                content: f.url,
                dir_id: dirId,
                name: name || undefined,
              });
            }))
              .then(() => resolve({created: list.length}))
              .catch(reject);
          },
          onFail: (err) => reject(err),
        }, {coskey});
      });
    },
    handleUploadFiles(files) {
      const list = Array.isArray(files) ? files : [];
      if (!list.length) return;
      const dirId = this.resolveUploadDirId();
      this.uploadDirOverride = null;
      if (!dirId) {
        this.$Message.warning('无法确定上传目录');
        return;
      }

      const textLike = [];
      const binary = [];
      list.forEach((file) => {
        const docType = this.inferUploadDocType(file);
        if (docType === 'text' || docType === 'md') {
          textLike.push(file);
        } else {
          binary.push(file);
        }
      });

      this.uploading = true;
      const inlinePromises = textLike.map((file) => this.createKnowledgeRecordFromFile(file, dirId));

      Promise.all(inlinePromises)
        .then(() => {
          if (!binary.length) return {created: textLike.length};
          return this.uploadBinaryFilesToDir(binary, dirId).then((res) => ({
            created: textLike.length + (res && res.created ? res.created : 0),
            skipped: res && res.skipped,
          }));
        })
        .then((res) => {
          const created = res && res.created != null ? res.created : list.length;
          if (res && res.skipped) {
            this.$Message.warning(`部分文件未上传（${res.skipped} 个需配置存储）`);
          } else {
            this.$Message.success(`成功上传 ${created} 个文件`);
          }
          this.loadTree();
        })
        .catch((err) => {
          this.$Message.error(`上传失败: ${err}`);
        })
        .finally(() => {
          this.uploading = false;
        });
    },
    createDocument(dir, docType) {
      const type = docType === 'text' ? 'text' : 'md';
      const payload = {
        type,
        name: type === 'md' ? '未命名文档' : '未命名文本',
        content: '',
        dir_id: dir.id,
        markdown_content: type === 'md' ? '' : undefined,
        has_markdown: type === 'md',
      };
      this.$api.post('/knowledge/data', payload)
        .then((res) => {
          const body = res && res.data;
          const created = body && body.data != null ? body.data : body;
          this.$Message.success('创建成功');
          this.loadTree();
          if (created && created.id != null) {
            this.$nextTick(() => {
              const node = {id: created.id, type, name: created.name, dir_id: dir.id};
              this.handleDocSelect(node, true);
            });
          }
        })
        .catch((err) => this.$Message.error(`创建失败: ${err}`));
    },
    deleteDirectory(dir) {
      return this.$api.delete(`/knowledge/directories/${dir.id}`)
        .then(() => {
          this.$Message.success('已删除');
          this.loadTree();
        })
        .catch((err) => {
          this.$Message.error(`删除失败: ${err}`);
        });
    },
    deleteFile(file) {
      const id = file && file.id;
      if (id == null) return Promise.resolve();
      return this.$api.delete(`/knowledge/data/${id}`)
        .then(() => {
          this.$Message.success('已删除');
          if (String(this.selectedDocId) === String(id)) {
            this.selectedDocId = '';
            this.currentDoc = null;
            this.editContent = '';
            this.contentDirty = false;
          }
          this.loadTree();
        })
        .catch((err) => {
          this.$Message.error(`删除失败: ${err}`);
        });
    },
    onTreeDocSelect(doc) {
      this.handleDocSelect(doc, true);
    },
    handleDocSelect(doc, pushQuery = true) {
      if (!doc || doc.id == null || doc.id === '') return;
      if (this.contentDirty || this.nameDirty) {
        const parts = [];
        if (this.nameDirty) parts.push('名称');
        if (this.contentDirty) parts.push('内容');
        this.$Modal.confirm({
          title: '未保存的更改',
          content: `当前文档${parts.join('、')}有未保存的修改，切换后将丢失，是否继续？`,
          onOk: () => {
            this.applyDocSelect(doc, pushQuery);
            this.closeTreeDrawer();
          },
        });
        return;
      }
      this.applyDocSelect(doc, pushQuery);
      this.closeTreeDrawer();
    },
    applyDocSelect(doc, pushQuery) {
      this.selectedDocId = doc.id;
      this.contentDirty = false;
      this.contentReady = false;
      this.loadDocumentContent(doc);
      if (pushQuery) {
        const query = {...this.$route.query, doc: doc.id};
        if (this.$route.query.doc !== String(doc.id)) {
          this.$router.replace({query}).catch(() => {});
        }
      }
    },
    syncEditBaseline() {
      this.contentBaseline = this.editContent || '';
      this.nameBaseline = (this.editName || '').trim();
      this.contentDirty = false;
      this.contentReady = true;
    },
    markContentDirtyIfChanged() {
      if (!this.contentReady) return;
      this.contentDirty = (this.editContent || '') !== (this.contentBaseline || '');
    },
    onEditContentInput() {
      this.markContentDirtyIfChanged();
    },
    buildSavePayload({includeName = false, includeContent = false} = {}) {
      const type = this.editKind;
      const payload = {
        ...this.currentDoc,
        type: type === 'text' ? 'text' : (this.currentDoc.type || 'md'),
      };
      if (includeName) {
        payload.name = (this.editName || '').trim();
      }
      if (includeContent) {
        payload.content = this.editContent;
        if (type === 'md') {
          payload.markdown_content = this.editContent;
          payload.has_markdown = true;
        }
      }
      return payload;
    },
    applySaveResult(payload, {savedName = false, savedContent = false} = {}) {
      this.currentDoc = {...this.currentDoc, ...payload};
      if (savedName) {
        const name = (payload.name || '').trim();
        this.nameBaseline = name;
        this.tree = patchTreeNode(this.tree, this.currentDoc.id, {name});
      }
      if (savedContent) {
        this.contentBaseline = this.editContent || '';
        this.contentDirty = false;
      }
    },
    saveDocumentName() {
      if (!this.currentDoc || !this.nameDirty) return;
      const name = (this.editName || '').trim();
      if (!name) {
        this.$Message.warning('请输入文档名称');
        return;
      }
      const alsoSaveContent = this.contentDirty;
      const payload = this.buildSavePayload({
        includeName: true,
        includeContent: alsoSaveContent,
      });
      this.nameSaving = true;
      this.$api.put(`knowledge/data/${this.currentDoc.id}`, payload)
        .then(() => {
          this.applySaveResult(payload, {
            savedName: true,
            savedContent: alsoSaveContent,
          });
          this.$Message.success(alsoSaveContent ? '文档已保存' : '名称已保存');
        })
        .catch((err) => this.$Message.error(`保存失败: ${err}`))
        .finally(() => {
          this.nameSaving = false;
        });
    },
    loadDocumentContent(doc) {
      const id = doc && doc.id;
      if (id == null || id === '') {
        this.currentDoc = null;
        this.editContent = '';
        this.editName = '';
        this.contentReady = false;
        return;
      }
      this.contentLoading = true;
      this.contentReady = false;
      this.editorMountKey += 1;
      this.$api.afterLogin(() => {
        this.$api.get(`knowledge/data/${id}`)
          .then((res) => {
            const body = res && res.data;
            const detail = body && body.data != null ? body.data : body;
            this.currentDoc = detail || {...doc};
            this.editName = this.currentDoc.name || '';
            const type = String(this.currentDoc.type || '').toLowerCase();
            if (type === 'text') {
              this.editContent = this.currentDoc.content || '';
            } else {
              this.editContent = getMarkdownContent(this.currentDoc) || '';
            }
            this.$nextTick(() => {
              this.$nextTick(() => {
                this.syncEditBaseline();
              });
            });
          })
          .catch((err) => {
            this.currentDoc = {...doc};
            this.editName = doc.name || '';
            this.editContent = '';
            this.syncEditBaseline();
            this.$Message.error(`加载文档失败: ${err}`);
          })
          .finally(() => {
            this.contentLoading = false;
          });
      });
    },
    saveDocument() {
      if (!this.currentDoc || !this.canEdit) return;
      const alsoSaveName = this.nameDirty;
      const payload = this.buildSavePayload({
        includeName: alsoSaveName,
        includeContent: true,
      });
      if (alsoSaveName && !(payload.name || '').trim()) {
        this.$Message.warning('请输入文档名称');
        return;
      }
      this.saving = true;
      this.$api.put(`knowledge/data/${this.currentDoc.id}`, payload)
        .then(() => {
          this.applySaveResult(payload, {
            savedName: alsoSaveName,
            savedContent: true,
          });
          this.$Message.success(alsoSaveName ? '文档已保存' : '内容已保存');
        })
        .catch((err) => {
          this.$Message.error(`保存失败: ${err}`);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    goView() {
      if (this.embedDirId) {
        this.$emit('go-preview', this.selectedDocId);
        return;
      }
      this.$router.push({
        path: `/core/documents/${this.directoryId}/view`,
        query: this.selectedDocId ? {doc: this.selectedDocId} : {},
      });
    },
  },
};
</script>

<style lang="less" scoped>
@import './documents-tools-mobile.less';

.documents-page {
  height: 100%;
  min-height: 100%;
}

.documents-bar-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.documents-bar-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.documents-bar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  padding-right: 8px;

  .documents-action-btn {
    /deep/ .ivu-icon + span,
    /deep/ span + .ivu-icon {
      margin-left: 4px;
    }
  }
}

.documents-editor-body-wrap {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.documents-editor-main {
  flex: 1;
  min-height: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.documents-editor-state {
  flex: 1;
  align-self: stretch;
  width: 100%;
  min-height: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.documents-editor-state--empty {
  background: rgba(120, 120, 120, 0.14);

  /deep/ .l-empty-holder {
    width: 100%;
    height: 100%;
    min-height: 100%;
    opacity: 0.55;
    filter: grayscale(1);
  }

  /deep/ .l-empty-message {
    color: #909399;
  }
}

.documents-editor-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px 20px 20px;
  box-sizing: border-box;
}

.documents-editor-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.documents-editor-save-btn {
  margin-left: auto;
  flex-shrink: 0;
}

.documents-editor-name-input {
  flex: 1;
  min-width: 0;
  max-width: 480px;

  /deep/ .ivu-input {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    border-color: transparent;
    background: transparent;
    box-shadow: none;
    padding-left: 0;
  }

  /deep/ .ivu-input:hover,
  /deep/ .ivu-input:focus {
    border-color: #dcdee2;
    background: #fff;
    padding-left: 7px;
  }
}

.documents-editor-dirty {
  font-size: 12px;
  color: var(--warning);
}

.documents-editor-body {
  flex: 1;
  min-height: 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.documents-editor-textarea {
  flex: 1;
  min-height: 0;

  /deep/ textarea {
    height: calc(100vh - 170px) !important;
    min-height: 320px;
    font-family: Consolas, 'Courier New', monospace;
    font-size: 14px;
    line-height: 1.65;
    resize: vertical;
  }
}

.documents-modal-footer {
  text-align: right;
  padding-top: 8px;

  .ivu-btn + .ivu-btn {
    margin-left: 8px;
  }
}
</style>
